export interface IGetServerErrorMessageOptions {
  defaultMessage: string;
}

const DEFAULT_OPTIONS: IGetServerErrorMessageOptions = {
  defaultMessage: 'Error',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function extractErrorStringsDeep(value: unknown, acc: string[]): void {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) acc.push(trimmed);
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) extractErrorStringsDeep(item, acc);
    return;
  }

  if (isRecord(value)) {
    for (const v of Object.values(value)) extractErrorStringsDeep(v, acc);
  }
}

/**
 * The backend sometimes encodes internal exception metadata into the message using
 * the format: $$errorKey:message (optionally with $$ separators).
 *
 * We always strip the metadata and return the user-facing message portion.
 */
export function stripInternalErrorPrefix(message: string): string {
  const trimmed = message.trim();
  if (!trimmed.startsWith('$$')) return trimmed;

  // $$errorKey:message
  let decoded = trimmed.slice(2);
  const parts = decoded.split(':', 2);
  decoded = parts.length === 2 ? parts[1] : decoded;

  // Some messages include extra $$ separators: ...$$<userMessage>
  if (decoded.includes('$$')) {
    decoded = decoded.split('$$', 2)[1] ?? decoded;
  }

  return decoded.trim();
}

/**
 * Extract the most user-relevant message from API errors, supporting Axios and
 * fetch-like shapes used across the project.
 */
export function getServerErrorMessage(
  error: unknown,
  options?: Partial<IGetServerErrorMessageOptions>
): string {
  const { defaultMessage } = { ...DEFAULT_OPTIONS, ...(options ?? {}) };

  const err = isRecord(error) ? error : undefined;
  const response = err && isRecord(err.response) ? err.response : undefined;

  // Axios: error.response.data
  // $fetch-like: error.response._data
  const responseData =
    (response && (response._data as unknown)) ||
    (response && (response.data as unknown));

  // If the server returned a plain string, it's the message.
  if (typeof responseData === 'string') {
    const msg = responseData.trim();
    return msg ? stripInternalErrorPrefix(msg) : defaultMessage;
  }

  if (isRecord(responseData)) {
    // Validation errors: { errors: { field: [..] } } or nested structures.
    if ('errors' in responseData && responseData.errors !== undefined) {
      const strings: string[] = [];
      extractErrorStringsDeep(responseData.errors, strings);
      const joined = strings.join(', ').trim();
      if (joined) return stripInternalErrorPrefix(joined);
    }

    // NestJS standard: { message: string | string[] }
    if ('message' in responseData) {
      const msgValue = responseData.message;
      if (typeof msgValue === 'string' && msgValue.trim()) {
        return stripInternalErrorPrefix(msgValue);
      }
      if (Array.isArray(msgValue)) {
        const strings: string[] = [];
        extractErrorStringsDeep(msgValue, strings);
        const joined = strings.join(', ').trim();
        if (joined) return stripInternalErrorPrefix(joined);
      }
    }
  }

  // Preserve existing behavior: show CustomError.message (business-level) over defaultMessage.
  if (
    error instanceof Error &&
    error.name === 'CustomError' &&
    typeof error.message === 'string' &&
    error.message.trim()
  ) {
    return stripInternalErrorPrefix(error.message);
  }

  // Preserve existing behavior from the previous implementation:
  // prefer caller-provided defaultMessage over generic Axios Error.message.
  if (typeof defaultMessage === 'string' && defaultMessage.trim()) {
    return defaultMessage;
  }

  // Fallback to any root-level message if defaultMessage is empty.
  if (error instanceof Error && typeof error.message === 'string' && error.message.trim()) {
    return stripInternalErrorPrefix(error.message);
  }
  if (err && typeof err.message === 'string' && err.message.trim()) {
    return stripInternalErrorPrefix(err.message);
  }

  return DEFAULT_OPTIONS.defaultMessage;
}

